/** @jsxImportSource @emotion/react */
import classNames from 'classnames';
import { appFeature } from 'modules/organizer/domain/interfaces/available-feature';
import React, { type ReactElement, useMemo } from 'react';
import { useMatch } from 'react-router-dom';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { useFeature } from 'shared/hooks/use-feature';
import { theme } from 'styles/theme/theme';
import { UDText } from 'ud-ui/components';
import UDNavLink from '../../../link/nav-link';
import UDTippy from '../../../tippy';

interface IProps {
  to?: string;
  checkPath?: string;
  icon: ReactElement;
  label: string;
  tooltip?: string;
  disabled?: boolean;
  onClick?: () => void;
  adminOnly?: boolean;
  hide?: boolean;
  feature?: appFeature;
  badge?: {
    label?: string;
    icon?: ReactElement;
    color?: string;
  };
}

interface IFeatureProps extends IProps {
  feature: appFeature;
}

function FeatureMenuItem(props: IFeatureProps) {
  const { isEnabled, isFetching } = useFeature(props.feature);
  const featureDisabled = isFetching || !isEnabled;
  return <MenuItemInner {...props} featureDisabled={featureDisabled} />;
}

interface IInnerProps extends IProps {
  featureDisabled: boolean;
}

function MenuItemInner(props: IInnerProps) {
  const { to, checkPath, icon, label, tooltip, disabled, onClick, adminOnly, badge, hide, featureDisabled } = props;
  const isAdmin = useIsAdmin();

  const effectiveDisabled = disabled || featureDisabled;
  const effectiveTooltip = featureDisabled ? 'Функционал недоступен в вашей подписке' : tooltip;

  const url = checkPath ?? (typeof to === 'string' ? to : '');
  const isActive = useMatch(url ?? '');

  const component = useMemo(() => {
    if (!to) {
      return (
        <div
          onClick={onClick}
          style={{ height: 36, cursor: 'pointer' }}
          className={classNames('link secondary text text subhead flex items-center px-10', {
            'gap-2': !!icon,
            active: isActive,
          })}
        >
          <div className={'flex-center'}>{icon}</div>
          {label}
        </div>
      );
    }

    return (
      <UDNavLink
        to={to}
        checkPath={checkPath ?? to + '/*'}
        style={{ height: 36 }}
        className={classNames('nav-item text subhead flex items-center px-10', {
          'gap-2': !!icon,
        })}
        variant={'secondary'}
        disabled={effectiveDisabled}
      >
        <div className={'flex-center'}>{icon}</div>
        {label}
        {badge && (
          <div
            style={{
              minWidth: 20,
              height: 20,
              borderRadius: 4,
              backgroundColor: badge.color ?? theme.colors.Blue,
            }}
            className="flex-center"
          >
            {badge.icon && (badge.icon as ReactElement)}
            {badge.label && (
              <UDText type={'text-1'} isNew color={theme.colors.DarkYellow}>
                {badge.label}
              </UDText>
            )}
          </div>
        )}
      </UDNavLink>
    );
  }, [badge, checkPath, effectiveDisabled, icon, isActive, label, onClick, to]);

  if ((adminOnly && !isAdmin) || hide) {
    return null;
  }

  if (!effectiveTooltip) {
    return component;
  }

  return (
    <UDTippy content={effectiveTooltip} placement={'bottom'}>
      <div>{component}</div>
    </UDTippy>
  );
}

function MenuItem(props: IProps) {
  if (props.feature) {
    return <FeatureMenuItem {...props} feature={props.feature} />;
  }
  return <MenuItemInner {...props} featureDisabled={false} />;
}

export default MenuItem;
